'use client';

import type { ClassValue } from 'clsx';
import type { ReactNode } from 'react';

import {
  MegamenuClose,
  MegamenuIndicator,
  MegamenuItem,
  MegamenuPanel,
  MegamenuTrigger,
  MegamenuTriggerLabel,
  useMegamenuDisclosure,
} from '@vs-shop/commerce-ui/megamenu';
import { clsxm } from '@vs-shop/utils';
import { X } from 'lucide-react';
import { useIntl } from 'react-intl';

import { MEGAMENU_DEBOUNCE_MILLIS } from '../../../../config/navigation';
import {
  megamenuRowIndicatorVariants,
  megamenuRowWordVariants,
} from '../../../server/navigation/desktop/megamenuLayout';
import {
  resolveTaxonNameForDisplay,
  resolveTaxonNameForMetadata,
} from '@vs-shop/domain/taxon/resolveTaxonName';

import Link from '@vs-shop/image-link/Link';

import type { TaxonMenu } from '../../../../effect/schema/openapi/backend';

type Props = {
  taxon: TaxonMenu;
  megamenu: ReactNode;
  /** Whether this is the row's leading word, which sits clear of the container edge. */
  inset: 'leading' | 'flush';
  className?: ClassValue;
};

export default function NavigationListItem({ taxon, megamenu, inset, className }: Props) {
  const intl = useIntl();
  const { state, itemRef, openAfterDelay, closeNow, closeOnAction, closeWhenFocusLeaves } =
    useMegamenuDisclosure({ openDelayMillis: MEGAMENU_DEBOUNCE_MILLIS });

  const triggerId = `megamenu-trigger-${taxon.code}`;
  const panelId = `megamenu-panel-${taxon.code}`;
  const title = resolveTaxonNameForMetadata(taxon);

  return (
    <MegamenuItem
      ref={itemRef}
      className={clsxm(className)}
      onClick={closeOnAction}
      onFocus={openAfterDelay}
      onBlur={closeWhenFocusLeaves}
      // The item spans the word AND its panel, so a pointer travelling from one
      // to the other never leaves the element the open state hangs off.
      onMouseEnter={openAfterDelay}
      onMouseLeave={closeNow}
    >
      <MegamenuTrigger
        className={megamenuRowWordVariants({ variant: 'category', inset })}
        id={triggerId}
        aria-controls={panelId}
        aria-expanded={state === 'open'}
        data-state={state}
        onPointerDown={closeNow}
        onClick={closeNow}
        render={
          // TODO: API - element={element}
          <Link href={taxon.routerUrl} title={title} prefetch={false} />
        }
      >
        <MegamenuTriggerLabel>{resolveTaxonNameForDisplay(taxon)}</MegamenuTriggerLabel>
        <MegamenuIndicator
          reveal="open"
          className={megamenuRowIndicatorVariants({ variant: 'category' })}
        />
      </MegamenuTrigger>

      <MegamenuPanel
        id={panelId}
        data-state={state}
        aria-hidden={state === 'closed'}
        aria-label={intl.formatMessage(
          {
            id: 'component.client.navigation.desktop.navigationListItem.panelLabel',
            defaultMessage: '{taxonName} menu',
          },
          { taxonName: taxon.name }
        )}
      >
        {megamenu}

        <MegamenuClose
          onClick={closeNow}
          aria-label={intl.formatMessage({
            id: 'component.client.navigation.desktop.navigationListItem.close',
            defaultMessage: 'Zavřít',
          })}
        >
          <X />
        </MegamenuClose>
      </MegamenuPanel>
    </MegamenuItem>
  );
}
